import BackIcon from '@mui/icons-material/ArrowBack';
import { useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import ContentView from '~/components/ContentView';
import FolderForm from '~/components/FolderForm';
import { useCreateFolder } from '~/hooks/api/useCreateFolder';
import { useFetchFolderContent } from '~/hooks/api/useFetchFolderContent';
import Spacings from '~/modules/ui-kit/spacings';
import { FolderPurpose } from '~/generated';
import messages from './messages';
import styles from './styles.module.css';

const route = getRouteApi('/documents/$folderId/newFolder');

const FolderCreate = () => {
  const theme = useTheme();
  const params = route.useParams();
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const search = route.useSearch();
  const navigate = useNavigate();
  const parentFolderId = params.folderId;
  const { data: fetched, isFetching } = useFetchFolderContent(parentFolderId);
  const handleBackButton = () => {
    navigate({ to: search.from || '/documents' });
  };
  const createFolderMutation = useCreateFolder({
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.saveFolderSuccess),
      });
      handleBackButton();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.saveFolderFailure),
      });
    },
  });

  const getFolderName = () => {
    if (isFetching || !fetched) {
      return null;
    }
    if (parentFolderId === 'root') {
      return intl.formatMessage(messages.rootFolder);
    }
    return fetched.folder?.name;
  };

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Spacings.Inline alignItems="center">
              <IconButton
                color="inherit"
                onClick={handleBackButton}
                size="large"
              >
                <BackIcon data-testid="back" />
              </IconButton>
              <div style={{ marginLeft: theme.spacing(2) }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </div>
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={createFolderMutation.isPending}
    >
      <Spacings.Stack scale="xl" className={styles.form}>
        <div>
          <Typography
            variant="caption"
            sx={{
              color: 'inherit',
            }}
          >
            <FormattedMessage {...messages.currentFolder} />
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'inherit',
            }}
          >
            {getFolderName()}
          </Typography>
        </div>
        <div>
          {isFetching ? null : (
            <FolderForm
              onSubmit={(data) =>
                createFolderMutation.mutate({
                  parentFolderId: parentFolderId,
                  data: {
                    name: data.name,
                    purpose: parseInt(data.purpose, 10) as FolderPurpose,
                    contentExpirationInDays: parseInt(
                      data.contentExpirationInDays,
                      10
                    ),
                  },
                })
              }
            />
          )}
        </div>
      </Spacings.Stack>
    </ContentView>
  );
};

export default FolderCreate;
